import { CAREERS_OFFICE_ALT, CAREERS_OFFICE_TITLE } from "@/lib/careers-seo";
import type { CareersLang } from "./careers-constants";

export interface CareersProcessStep {
  readonly title: string;
  readonly body: string;
}

export interface CareersProcessCopy {
  readonly title: string;
  readonly subtitle: string;
  readonly steps: readonly CareersProcessStep[];
}

/** All copy on the careers page, per language. Texts mirror the pre-redesign
 * page 1:1; only the growth section (from /careers/developers)
 * needed a German translation. */
export interface CareersContent {
  readonly bar: { readonly cta: string; readonly ctaShort: string };
  readonly hero: {
    readonly title: string;
    readonly intro: string;
    readonly introMobile: string;
    readonly facts: readonly string[];
    readonly cta: string;
  };
  readonly work: {
    readonly title: string;
    readonly paragraphs: readonly string[];
    readonly tags: readonly string[];
    readonly more: string;
  };
  readonly growth: {
    readonly title: string;
    readonly intro: string;
    readonly callout: string;
  };
  readonly location: {
    readonly imageAlt: string;
    readonly imageTitle: string;
    readonly caption: string;
  };
  readonly process: CareersProcessCopy;
  readonly teaser: {
    readonly accent: string;
    readonly rest: string;
    readonly cta: string;
  };
  readonly jobs: { readonly title: string };
  readonly footer: {
    readonly youtube: string;
    readonly imprint: string;
    readonly privacy: string;
  };
}

export const CAREERS_CONTENT: Record<CareersLang, CareersContent> = {
  de: {
    bar: { cta: "Alle Jobs ansehen", ctaShort: "Jobs" },
    hero: {
      title: "Deine Karriere bei hausverwalter.ai",
      intro:
        "Wir bauen DIE All-in-One-Software für Hausverwaltungen. Um diesen Weg weiterzugehen, suchen wir motivierte Menschen, die mit uns wachsen wollen. Bei uns übernimmst du vom ersten Tag an Verantwortung: kurze Wege, echter Gestaltungsspielraum und ein Team, das zusammenhält.",
      introMobile:
        "Wir bauen die All-in-One-Software für Hausverwaltungen. Verantwortung ab Tag eins, kurze Wege und ein Team, das zusammenhält.",
      facts: ["Standort Worms", "2 Tage Homeoffice", "Attraktives Gehalt"],
      cta: "Offene Stellen ansehen",
    },
    work: {
      title: "Die Hausverwaltung Software, die die Branche neu denkt",
      paragraphs: [
        "hausverwalter.ai ist die Hausverwaltung Software für Vorgangssystem, Buchhaltung, Kundenportal und Dokumentenmanagement (DMS), inklusive KI-Agenten, Online-Eigentümerversammlung und Telefonanlage.",
        "Wir bauen daraus den neuen Standard für eine Branche, die bisher zwischen uralten Programmen und Papierstapeln feststeckt.",
      ],
      tags: [
        "Vorgangssystem",
        "Buchhaltung",
        "Kundenportal",
        "DMS",
        "Online-ETV",
        "Telefonanlage",
        "Übergabeformular",
      ],
      more: "und vieles mehr",
    },
    growth: {
      title: "Wir fangen gerade erst an",
      intro:
        "hausverwalter.ai ist 2026 gestartet. Die ersten Hausverwaltungen arbeiten bereits mit unserem Produkt und sind ehrlich begeistert, haben sogar in das Unternehmen investiert. Jetzt vergrößern wir das Team und machen uns bereit zu skalieren.",
      callout: "HIER STEIGST DU EIN",
    },
    location: {
      imageAlt: CAREERS_OFFICE_ALT,
      imageTitle: CAREERS_OFFICE_TITLE,
      caption: "Unser Standort in Worms",
    },
    process: {
      title: "Was dich in unserem Bewerbungsprozess erwartet",
      subtitle:
        "Wir wissen, dass eine Bewerbung Zeit und Energie kostet. Deshalb halten wir den Prozess transparent. Du weißt in jedem Schritt, woran du bist.",
      steps: [
        {
          title: "Prüfung deiner Bewerbung",
          body: "Ein paar kurze Fragen im Bewerbungsformular helfen uns, dich von Anfang an besser kennenzulernen. Nimm dir also einen Moment für deine Antworten. Unabhängig vom Ergebnis bekommst du von uns eine Rückmeldung.",
        },
        {
          title: "Video-Intro-Call",
          body: "In einem kurzen Video-Call lernen wir uns gegenseitig kennen: dein Hintergrund, deine Ziele und was dir bei deinem nächsten Schritt wichtig ist. Gleichzeitig kannst du uns alles fragen: zur Rolle, zum Produkt oder zur Arbeitsweise im Team.",
        },
        {
          title: "Skill-Check",
          body: "Du bekommst eine praxisnahe Aufgabe, die typischen Aufgaben der Rolle nachempfunden ist, mit klaren Zielen und nachvollziehbaren Anforderungen. Plane dafür etwa einen Arbeitstag ein.",
        },
        {
          title: "Video-Interview",
          body: "Nach dem Skill-Check gehen wir in die Tiefe: Wir sprechen über die Rolle selbst, deine bisherige Erfahrung und deine Arbeitsweise. Ein offenes Gespräch auf Augenhöhe, mit ehrlichen Einblicken und Raum für deine Fragen.",
        },
      ],
    },
    teaser: {
      accent: "Softwareentwickler",
      rest: " Hier entlang bitte",
      cta: "Zur Entwickler-Seite",
    },
    jobs: { title: "Unsere offenen Stellen" },
    footer: {
      youtube: "Produkt-Videos",
      imprint: "Impressum",
      privacy: "Datenschutz",
    },
  },
  en: {
    bar: { cta: "View all jobs", ctaShort: "Jobs" },
    hero: {
      title: "Your Career at hausverwalter.ai",
      intro:
        "We're building THE all-in-one software for property management companies. To keep pushing this forward, we're looking for motivated people who want to grow with us. From day one, you'll take on real responsibility: short paths, real room to shape things, and a team that has your back.",
      introMobile:
        "We're building the all-in-one software for property management. Ownership from day one: short paths, real room to shape things, and a team that has your back.",
      facts: ["Office in Worms", "2 days home office", "Attractive salary"],
      cta: "View Open Positions",
    },
    work: {
      title: "The software that reimagines property management",
      paragraphs: [
        "hausverwalter.ai is the software property management companies use to handle everything in one place: communication, documents, video calls, and AI agents in a single interface.",
        "We're turning that into the new standard for an industry that's been stuck between ancient software and paper stacks.",
      ],
      tags: [
        "Communication",
        "All residents",
        "Documents",
        "Video Calls",
        "AI Agents",
        "Team Chat",
      ],
      more: "and much more",
    },
    growth: {
      title: "We're just getting started",
      intro:
        "hausverwalter.ai launched in 2026. The first property management companies already use our product and are genuinely excited about it, even invested into the company. We're growing the team and are ready to scale.",
      callout: "JOIN US HERE",
    },
    location: {
      imageAlt: "Our office in Worms and a colleague at work",
      imageTitle: "hausverwalter.ai office in Worms, Germany",
      caption: "Our office in Worms, Germany",
    },
    process: {
      title: "What to expect in our application process",
      subtitle:
        "We know that applying takes time and energy. That's why we keep the process transparent. You'll know exactly where you stand at every step.",
      steps: [
        {
          title: "Application Review",
          body: "A few short questions in the application form help us get to know you from the start. Take a moment with your answers. Either way, you'll hear back from us.",
        },
        {
          title: "Video Intro Call",
          body: "In a short video call, we get to know each other: your background, your goals, and what matters to you in your next step. You can ask us anything too: about the role, the product, or how the team works.",
        },
        {
          title: "Skill Check",
          body: "You'll get a hands-on task modeled on the role's actual day-to-day work, with clear goals and reasonable requirements. Plan for about a working day.",
        },
        {
          title: "Video Interview",
          body: "After the skill check, we go deeper: we talk about the role itself, your experience so far, and how you work. An open conversation between equals, with honest insights and room for your questions.",
        },
      ],
    },
    teaser: {
      accent: "Software Engineer",
      rest: " This way please",
      cta: "Go to Developer Page",
    },
    jobs: { title: "Our Open Positions" },
    footer: {
      youtube: "Product videos",
      imprint: "Imprint",
      privacy: "Privacy",
    },
  },
};
